import React, { FC } from 'react';
import { Field } from '@chakra-ui/react';
const ChakraFormControl = Field.Root;
import { FormControlProps } from './types';
import { FormLabel } from '../FormLabel/component';
import { HelpText } from '../HelpText/component';
import { ErrorMessage } from '../ErrorMessage/component';

/**
 * Core FormControl — wrapper that composes label, help text, and error message
 * around any form input child, and provides Chakra's isInvalid/isDisabled/isRequired
 * context to all descendants.
 */
export const FormControl: FC<FormControlProps> = ({
  'aria-label': ariaLabel,
  children,
  direction = 'column',
  disabled,
  errorMessage,
  helpText,
  helpTextPosition = 'after',
  hideLabel = false,
  id,
  invalid,
  isFieldset = false,
  isInvalid,
  isDisabled,
  isReadOnly,
  isRequired,
  label,
  labelClassName,
  name,
  readOnly,
  required,
  tooltip,
  ...rest
}) => {
  // Normalise legacy position aliases so callers can use 'top'/'bottom' or 'before'/'after'
  const normalizedPosition =
    helpTextPosition === 'top' ? 'before' : helpTextPosition === 'bottom' ? 'after' : helpTextPosition;

  // The legacy name wins on conflict.
  const resolvedInvalid = isInvalid ?? invalid;
  const resolvedDisabled = isDisabled ?? disabled;
  const resolvedRequired = isRequired ?? required;
  const resolvedReadOnly = isReadOnly ?? readOnly;

  // Stable IDs for explicit aria-describedby on child inputs that need it spelled out
  // (e.g. non-Chakra controls like legacy react-select).
  const helpTextId = helpText ? `${name}-helptext` : undefined;
  const errorId = resolvedInvalid && errorMessage ? `${name}-error` : undefined;

  return (
    <ChakraFormControl
      id={id ?? name}
      as={isFieldset ? 'fieldset' : 'div'}
      aria-label={ariaLabel}
      disabled={resolvedDisabled}
      invalid={resolvedInvalid}
      required={resolvedRequired}
      readOnly={resolvedReadOnly}
      {...rest}
    >
      {label && !hideLabel && (
        <FormLabel
          label={label}
          id={`${name}-label`}
          htmlFor={isFieldset ? undefined : name}
          asLegend={isFieldset}
          tooltip={tooltip}
          className={labelClassName}
        />
      )}
      {normalizedPosition === 'before' && helpText && <HelpText id={helpTextId}>{helpText}</HelpText>}
      {children}
      {normalizedPosition === 'after' && helpText && <HelpText id={helpTextId}>{helpText}</HelpText>}
      {resolvedInvalid && errorMessage && <ErrorMessage id={errorId} errorMessage={errorMessage} />}
    </ChakraFormControl>
  );
};

FormControl.displayName = 'FormControl';
